import { FundExpenseDetails } from '@/models/FundExpenseDetails';
import { SupplierFullDtoIn } from '@/models/in/SupplierFullDtoIn';

/**
 * Resolved supplier identity for a fund-expense support request.
 * Hybrid resolution: prefers the linked Supplier entity (new schema);
 * falls back to the legacy snapshot stored in SupportRequest.additionalDetails for
 * requests created before the Suppliers table existed.
 *
 * Bank fields are not included here - bank info lives on PaymentInfo and is surfaced
 * separately via `getPaymentBankDetails`.
 */
export interface ResolvedSupplierDetails {
  name?: string;
  taxId?: string;
}

export function getSupplierDisplayDetails(
  supplier: SupplierFullDtoIn | null | undefined,
  additionalDetails: object | undefined
): ResolvedSupplierDetails | null {
  if (supplier) {
    return {
      name: supplier.name,
      taxId: supplier.taxId,
    };
  }

  const legacy = additionalDetails as FundExpenseDetails | undefined;
  if (!legacy) return null;

  return {
    name: legacy.supplierName,
    taxId: legacy.supplierTaxId,
  };
}

/**
 * Resolved bank details for the destination of a fund-expense payment:
 *  - isAlreadyPaid=false: vendor's bank (from the linked PaymentInfo on the request - the
 *    request DTO exposes these as bankNumber/bankBranchNumber/bankAccountNumber).
 *  - isAlreadyPaid=true: the payer's bank from AdditionalDetails (`payerBank*`), with a
 *    fallback to the legacy `bank*` fields on AdditionalDetails for requests created before
 *    the payer/vendor split was implemented (back then both bank sets were the same field).
 *
 * Vendor bank fields are passed in explicitly because they live on PaymentInfo (per fund +
 * supplier), not on the Supplier row itself.
 */
export interface ResolvedPaymentBankDetails {
  bankCode?: number | null;
  bankBranch?: string | null;
  bankAccount?: string | null;
}

export function getPaymentBankDetails(
  vendorBank: { bankCode?: number | null; bankBranch?: string | null; bankAccount?: string | null } | null | undefined,
  additionalDetails: object | undefined
): ResolvedPaymentBankDetails | null {
  const details = additionalDetails as FundExpenseDetails | undefined;
  const isAlreadyPaid = !!details?.isAlreadyPaid;

  if (isAlreadyPaid) {
    if (!details) return null;
    return {
      bankCode: details.payerBankCode ?? details.bankCode ?? null,
      bankBranch: details.payerBankBranch ?? details.bankBranch ?? null,
      bankAccount: details.payerBankAccount ?? details.bankAccount ?? null,
    };
  }

  if (vendorBank && (vendorBank.bankCode != null || vendorBank.bankBranch || vendorBank.bankAccount)) {
    return {
      bankCode: vendorBank.bankCode ?? null,
      bankBranch: vendorBank.bankBranch ?? null,
      bankAccount: vendorBank.bankAccount ?? null,
    };
  }

  if (!details) return null;
  return {
    bankCode: details.bankCode ?? null,
    bankBranch: details.bankBranch ?? null,
    bankAccount: details.bankAccount ?? null,
  };
}
